import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { validatorErrors } from 'common/class/data/validator/validator-errors';
import { PasswordToggle } from 'common/constant/PasswordToggle';
import { AWSChallenge } from 'common/constant/authentication/aws-challenge';
import { RoutePath } from 'common/constant/route-path';
import { Strings } from 'common/utility/string/string';
import { Subject, catchError, of, take, takeUntil } from 'rxjs';
import { AuthenticationService } from 'services/authentication/authentication.service';
import { LoginResult } from 'services/authentication/model/login-result';
import { LoadingService } from 'services/loading/loading.service';
import { OrganizationService } from 'services/organization/organization.service';
import { TranslationService } from 'services/translation/translation.service';
import { PasswordInputType } from '../../common/constant/PasswordInputType';

enum LoginStatus {
  NotAttempted,
  Attempting,
  Failed,
  Succeeded
}

const loginControls = {
  organizationId: 'organizationId',
  password: 'password',
  username: 'username'
};

const newPasswordControls = {
  newPassword: 'newPassword',
  repeatPassword: 'repeatPassword'
};

@Component({
  selector: 'ems-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {

  private passwordToggles: Array<boolean> = new Array(2).fill(false)
  private passwordChangeAttemptError: string | undefined

  private unsubscribe: Subject<void> = new Subject()


  private loginStatus: LoginResult | undefined
  private loginAttempt: LoginStatus = LoginStatus.NotAttempted
  private newPasswordRequired: boolean = false
  private showPasswordPolicy: boolean = false

  public form: FormGroup = this.createForm();
  public newPasswordForm: FormGroup = this.createNewPasswordForm();


  public get organizationId(): FormControl {
    return this.getFormControl(loginControls.organizationId);
  }

  public get username(): FormControl {
    return this.getFormControl(loginControls.username);
  }

  public get password(): FormControl {
    return this.getFormControl(loginControls.password);
  }

  public get newPassword(): FormControl {
    return this.getFormControl(newPasswordControls.newPassword, this.newPasswordForm);
  }

  public get repeatPassword(): FormControl {
    return this.getFormControl(newPasswordControls.repeatPassword, this.newPasswordForm);
  }

  public get NewPasswordRequired(): boolean {
    return this.newPasswordRequired
  }

  public get organizationIdError(): string {
    return this.form.pristine ? Strings.Empty :
      this.organizationId.hasError(validatorErrors.required) ? 'login.errors.organizationId' : 'empty'
  }

  public get usernameError(): string {
    return this.form.pristine
      ? Strings.Empty
      : this.username.hasError(validatorErrors.required) ? 'login.errors.username.required' : 'empty'
  }

  public get passwordError(): string {
    return this.form.pristine ? Strings.Empty :
      this.password.hasError(validatorErrors.required) ? 'login.errors.password' : 'empty'
  }

  public get ForgotPasswordLink(): string {
    return `/${RoutePath.PasswordForgot}`
  }

  public get FailedLogin(): boolean {
    return this.loginAttempt == LoginStatus.Failed
  }

  public get FailedPasswordChange(): boolean {
    return this.loginAttempt == LoginStatus.Failed
  }

  public get ButtonDisabled(): boolean {
    return this.loggedIn || this.invalidCredentials || this.loginAttempt == LoginStatus.Attempting
  }

  public get PasswordChangeButtonDisabled(): boolean {
    return this.invalidNewPasswords
  }

  public get validCredentials(): boolean {
    return this.username.valid && this.password.valid && this.organizationId.valid;
  }

  public get invalidCredentials(): boolean {
    return !this.validCredentials;
  }

  public get invalidNewPasswords(): boolean {
    return this.newPassword.invalid || this.repeatPassword.value !== this.newPassword.value
  }

  public get passwordToggle(): typeof PasswordToggle {
    return PasswordToggle
  }

  public get passwordChangeErrors(): boolean {
    return !!this.allPasswordChangeErrors.length
  }

  public get allPasswordChangeErrors(): Array<string> {
    const returnValue: Array<string> = [
      ...this.newPasswordErrors,
      ...this.repeatPasswordErrors,
    ]
    return returnValue
  }

  public get newPasswordErrors(): Array<string> {
    const returnValue = this.getPasswordErrors(this.newPassword)
    return returnValue
  }

  public get repeatPasswordErrors(): Array<string> {
    const returnValue = this.getPasswordErrors(this.repeatPassword)
    if (this.repeatPassword.value !== this.newPassword.value) returnValue.push(this.getError('no-match'))
    return returnValue
  }

  public get defaultError(): string {
    return this.getError('generic')
  }

  public get AttemptError(): string | undefined {
    return this.passwordChangeAttemptError
  }

  public get ShowPasswordPolicy(): boolean {
    return this.showPasswordPolicy && this.NewPasswordRequired
  }

  public SubmitClicked(): void {
    this.authenticate()
  }

  public PasswordChangeSubmit(): void {
    this.changePassword()
  }

  public togglePasswordVisibility(identifier: PasswordToggle): void {
    this.passwordToggles[identifier] = !this.passwordToggles[identifier]
  }

  public passwordVisible(identifier: PasswordToggle): boolean {
    return this.passwordToggles[identifier]
  }

  public passwordInputType(identifier: PasswordToggle): string {
    return this.passwordVisible(identifier) ? PasswordInputType.ClearText : PasswordInputType.MaskedInput
  }

  public togglePasswordPolicy(): void {
    this.showPasswordPolicy = !this.showPasswordPolicy
  }

  constructor(
    private dialog:MatDialog,
    private authenticationService: AuthenticationService,
    private formBuilder: FormBuilder,
    private router: Router,
    private loadingService: LoadingService,
    private organizationService: OrganizationService

  ) {
    this.resetPasswordFormErrors()
    this.dialog.closeAll()
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  ngOnInit(): void {
    this.authenticationService.logout(false)
  }

  private get loggedIn(): boolean {
    return !!this.loginStatus?.loggedIn
  }

  private authenticate(): void {
    if (this.invalidCredentials) return

    this.loadingService.loading()

    this.loginAttempt = LoginStatus.Attempting

    this.authenticationService.login(this.username.value, this.password.value, this.organizationId.value)
      .pipe(
        catchError(() => {
          return of(LoginResult.empty());
        }),
        take(1)
      )
      .subscribe((result: LoginResult) => {
        this.loadingService.loaded()
        this.processLoginStatus(result)
        this.checkPasswordChange(result)
      });
  }

  private checkPasswordChange(loginResult: LoginResult): void {
    this.newPasswordRequired = !!loginResult.CognitoUser && loginResult.CognitoUser.challengeName === AWSChallenge.NewPasswordRequired
    if (this.newPasswordRequired) {
      this.newPasswordForm.reset()
      this.passwordChangeAttemptError = undefined
    }
  }

  private changePassword(): void {
    if (!this.loginStatus?.CognitoUser) {
      return
    }

    this.loadingService.loading()
    this.loginStatus.CognitoUser.challengeParam.userAttributes['locale'] = TranslationService.CurrentLanguage.CountryCode

    this.authenticationService.changeFirstPassword(this.loginStatus.CognitoUser, this.newPassword.value)
      .pipe(
        catchError((error: any) => {
          this.passwordChangeAttemptError = this.getError(error?.code)
          return of(LoginResult.empty());
        }),
        take(1)
      )
      .subscribe((result: LoginResult) => {
        this.loadingService.loaded()
        if (!!result?.errorMessage && result.errorMessage === "InvalidPasswordException") {
          this.passwordChangeAttemptError = this.getError(result.errorMessage)
        } else {
          this.processLoginStatus(result)

          this.password.setValue(Strings.Empty)
          this.newPasswordRequired = false
          this.loginStatus = undefined
        }
      })
  }

  private processLoginStatus(result: LoginResult | undefined): void {
    this.loginStatus = result
    this.loginAttempt = this.loggedIn ? LoginStatus.Succeeded : LoginStatus.Failed

    if (!!this.loggedIn) {
      this.organizationService.Identifier = this.organizationId.value
      // this.router.navigate([RoutePath.Dashboard]);
      this.router.navigate([RoutePath.Report]);
    }
  }

  private createForm(): FormGroup {
    return this.formBuilder.group({
      organizationId: [null, [Validators.required]],
      username: [null, [Validators.required]],
      password: [null, [Validators.required]]
    });
  }

  private createNewPasswordForm(): FormGroup {
    return this.formBuilder.group({
      newPassword: [null, [Validators.required]],
      repeatPassword: [null, [Validators.required]]
    });
  }

  private getFormControl(controlName: string, form: FormGroup<any> = this.form): FormControl {
    return form.controls[controlName] as FormControl;
  }

  private getPasswordErrors(passwordControl: FormControl): Array<string> {
    const returnValue: Array<string> = []
    if (passwordControl.pristine) return returnValue
    if (passwordControl.hasError(validatorErrors.required)) returnValue.push(this.getError(validatorErrors.required))
    return returnValue
  }

  private getLabel(key: string): string {
    return `password.${key}`
  }

  private getError(key: string): string {
    return this.getLabel(`errors.${key}`)
  }

  private resetPasswordFormErrors(): void {
    this.newPasswordForm.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.passwordChangeAttemptError = undefined
      })
  }
}
